import { createBrowserRouter, Navigate, type RouteObject } from "react-router";

import { legacyRedirects } from "@/app/routes/legacyRedirects";
import NotFoundPage from "@/app/routes/NotFoundPage";
import { RouteError } from "@/app/routes/RouteError";
import { RequirePermission } from "@/app/session/RequirePermission";
import { FullScreenLoader, RequireSession } from "@/app/session/RequireSession";
import { AppShell } from "@/app/shell/AppShell";
import { GuestOnly } from "@/features/auth/components/GuestOnly";

type Lazy = () => Promise<{ default: React.ComponentType }>;

/** A lazily loaded page: each route ships as its own chunk. */
function page(path: string | undefined, load: Lazy): RouteObject {
  return {
    ...(path === undefined ? { index: true } : { path }),
    lazy: async () => ({ Component: (await load()).default }),
  };
}

/** A public auth screen: lazily loaded, and skipped once someone is signed in. */
function guestPage(path: string, load: Lazy): RouteObject {
  return {
    path,
    hydrateFallbackElement: <FullScreenLoader />,
    lazy: async () => {
      const Page = (await load()).default;
      return {
        Component: () => (
          <GuestOnly>
            <Page />
          </GuestOnly>
        ),
      };
    },
  };
}

export const router = createBrowserRouter([
  guestPage("/sign-in", () => import("@/features/auth/routes/SignInPage")),
  guestPage("/forgot-password", () => import("@/features/auth/routes/ForgotPasswordPage")),
  guestPage("/verify-code", () => import("@/features/auth/routes/VerifyCodePage")),
  guestPage("/new-password", () => import("@/features/auth/routes/NewPasswordPage")),
  ...legacyRedirects,
  // The office TV opens by a secret link, signed in or not (see TvController).
  {
    path: "/tv/:token",
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullScreenLoader />,
    lazy: async () => ({
      Component: (await import("@/features/targets/routes/TvPage")).default,
    }),
  },
  {
    element: <RequireSession />,
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullScreenLoader />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [
          page(undefined, () => import("@/features/dashboard/routes/HomePage")),
          {
            element: <RequirePermission permission="inventory.view" areaId="nav.units" />,
            children: [
              page("units", () => import("@/features/units/routes/UnitsPage")),
              page("units/:id", () => import("@/features/units/routes/UnitPage")),
            ],
          },
          {
            element: <RequirePermission permission="claim.view" areaId="nav.claims" />,
            children: [page("claims", () => import("@/features/units/routes/ClaimsPage"))],
          },
          {
            element: <RequirePermission permission="lead.view" areaId="nav.leads" />,
            children: [
              page("leads", () => import("@/features/clients/routes/LeadsPage")),
              page("leads/:id", () => import("@/features/clients/routes/ClientProfilePage")),
            ],
          },
          {
            element: <RequirePermission permission="lead.pool" areaId="nav.coldCalls" />,
            children: [
              page("cold-calls", () => import("@/features/leads/routes/ColdCallsPoolPage")),
              { path: "cold-calls/:id", element: <Navigate to="/cold-calls" replace /> },
            ],
          },
          {
            element: <RequirePermission permission="farming.view" areaId="nav.farming" />,
            children: [
              page("resale", () => import("@/features/clients/routes/FarmingPage")),
              page("resale/:id", () => import("@/features/clients/routes/ClientProfilePage")),
            ],
          },
          {
            element: <RequirePermission permission="pipeline.view" areaId="nav.pipeline" />,
            children: [page("pipeline", () => import("@/features/pipeline/routes/PipelinePage"))],
          },
          {
            element: <RequirePermission permission="request.view" areaId="nav.requests" />,
            children: [page("requests", () => import("@/features/requests/routes/RequestsPage"))],
          },
          {
            element: <RequirePermission permission="calendar-task.view" areaId="nav.calendar" />,
            children: [page("calendar", () => import("@/features/calendar/routes/CalendarPage"))],
          },
          {
            element: (
              <RequirePermission permission={["user.view", "role.view"]} areaId="nav.team" />
            ),
            children: [page("team", () => import("@/features/team/routes/TeamPage"))],
          },
          page("targets", () => import("@/features/targets/routes/TargetsPage")),
          page("reports", () => import("@/features/reports/routes/ReportsPage")),
          {
            element: <RequirePermission permission="tip.view" areaId="nav.tips" />,
            children: [page("tips", () => import("@/features/tips/routes/TipsPage"))],
          },
          {
            element: (
              <RequirePermission
                permission={["developer.create", "project.create"]}
                areaId="nav.catalog"
              />
            ),
            children: [
              page("setup/catalog", () => import("@/features/catalog/routes/CatalogPage")),
            ],
          },
          {
            element: (
              <RequirePermission
                permission={["status.create", "status.edit", "status.delete"]}
                areaId="nav.statuses"
              />
            ),
            children: [
              page("setup/statuses", () => import("@/features/catalog/routes/StatusesPage")),
            ],
          },
          {
            element: (
              <RequirePermission
                permission={[
                  "pipeline-stage.create",
                  "pipeline-stage.edit",
                  "pipeline-stage.delete",
                ]}
                areaId="nav.stages"
              />
            ),
            children: [page("setup/stages", () => import("@/features/catalog/routes/StagesPage"))],
          },
          {
            element: (
              <RequirePermission
                permission={["unit-tag.create", "unit-tag.edit", "unit-tag.delete"]}
                areaId="nav.unitTags"
              />
            ),
            children: [page("setup/tags", () => import("@/features/catalog/routes/UnitTagsPage"))],
          },
          {
            element: <RequirePermission permission="settings.edit" areaId="nav.workingHours" />,
            children: [
              page("setup/working-hours", () => import("@/features/leads/routes/WorkingHoursPage")),
            ],
          },
          {
            element: <RequirePermission permission="source.create" areaId="nav.sources" />,
            children: [
              page("setup/sources", () => import("@/features/catalog/routes/SourcesPage")),
            ],
          },
          {
            element: <RequirePermission permission="source.create" areaId="nav.campaigns" />,
            children: [
              page("setup/campaigns", () => import("@/features/catalog/routes/CampaignsPage")),
            ],
          },
          {
            element: <RequirePermission permission="source.create" areaId="nav.areas" />,
            children: [page("setup/areas", () => import("@/features/catalog/routes/AreasPage"))],
          },
          page("me", () => import("@/features/profile/routes/ProfilePage")),
          page("me/settings", () => import("@/features/profile/routes/ProfilePage")),
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
